import { useMemo } from "react";
import { object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { SummaryColumns } from "@repo/utils";
import { LOCKED_FEATURES } from "@utils";
import { useFeatureGuardian } from "@hooks";
import {
  useEstimateStructure,
  useEstimateEditorSettings,
  useEstimateEditorStore,
  PinnableCell,
  ValueCell,
  SummaryRow,
} from "@tools";
import { AddButtonFull } from "@components";

export const SummaryNewChildRow = observer(({
  workType,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { allowEdition } = useEstimateEditorSettings();
  
  const { isSellerOrClient } = useEstimateEditorSettings();

  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.ALLOCATION
  );

  const { displayTwoValues } = structure;
  const {
    visibleSummaryColumns,
    hasTeamMembersCol,
    hasTimeCol,
  } = structure.settings;
  
  const hasHoursCell = useMemo(
    () =>
      Boolean(
        visibleSummaryColumns.find((col) => col.name === SummaryColumns.HOURS)
      ),
    [visibleSummaryColumns]
  );
  
  const hasRateCell = useMemo(
    () =>
      Boolean(visibleSummaryColumns.find((col) => col.name === SummaryColumns.RATE)),
    [visibleSummaryColumns]
  );
  
  const hasCostCell = useMemo(
    () =>
      Boolean(visibleSummaryColumns.find((col) => col.name === SummaryColumns.COST)),
    [visibleSummaryColumns]
  );

  const handleNewResource = async () => {
    const allowed = await checkAccess();
    if (!allowed) return;
    structure.historyManager.startGroup();
    workType.addResource(true, `r${new Date().getTime()}`);
  };

  const tableDepth = 1;

  return (
    <SummaryRow
      useExpandCell
      isLastGroupRow
      tableDepth={tableDepth}
      displayMinMax={displayTwoValues}
    >
      <PinnableCell
        allowOverflowDisplay
        pinnedLeft
        innerStyle={{ paddingLeft: "var(--create-padding-1)" }}
      >
        <AddButtonFull
          id="newTeamMemberButton-summary"
          onClick={handleNewResource}
          name="Add new resource to work type"
        >
          {t("views.editor.summary_table.work_resource_add")}
        </AddButtonFull>
      </PinnableCell>
      { hasTeamMembersCol && <ValueCell size="medium" /> }
      { hasTimeCol && <ValueCell size="large" extendPadding={allowEdition || visibilityMode} /> }
      { hasRateCell && <ValueCell size="large" /> }
      { isSellerOrClient && hasHoursCell && <ValueCell size="large" /> }
      {
        isSellerOrClient && hasCostCell &&
        <ValueCell
          useDynamicWidth
          extendPadding
          colName={ SummaryColumns.COST }
          textPresetClass="preset-price"
        />
      }
      <FeatureAlert />
    </SummaryRow>
  );
});

SummaryNewChildRow.propTypes = {
  workType: object.isRequired,
};
